import def from "csi/def";
import FieldConstructor, { Field } from "csi/def/Field";
import { FormView } from "csi/form/View";
import Relationship from "csi/def/Relationship";
import Widget from "csi/form/Widget";
import locale from "csi/locale";
import csiStore from "csi/store/_base";
import Names from "csi/store/Names";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import array from "dojo/_base/array";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";
import _ from "csi/_base/underscore";
import { Context } from "csi/def/Context";
const PRODUCT_TYPE = "ProductType";
const FOOD_SUBTYPE = "FoodSubtype";
const FORMULATED_MATERIAL_SUBTYPE = "FormulatedMaterialSubtype";

const SelectBOMItemProductForm = declare([Widget], {

	/**
	 * @override
	 * Insert Product Type and Material Type fields
	 */
	_resolveView() {
		const fieldProductTypes = lang.mixin(new FieldConstructor(), {
			m_bRequired: true,
			m_label: locale.resource(PRODUCT_TYPE),
			m_typeControl: def.FIELD_FILTERING_SELECT,
			m_type: xml.ATTR_ENUM,
			m_onChange: "onChangeProductType",
		});

		const fieldFoodSubtypes = lang.mixin(new FieldConstructor(), {
			m_label: locale.resource(FOOD_SUBTYPE),
			m_typeControl: def.FIELD_FILTERING_SELECT,
		});

		const fieldFormulatedMaterialSubtypes = lang.mixin(new FieldConstructor(), {
			m_label: locale.resource(FORMULATED_MATERIAL_SUBTYPE),
			m_typeControl: def.FIELD_FILTERING_SELECT,
		});

		this.insertField(fieldProductTypes);
		this.insertField(fieldFoodSubtypes);
		this.insertField(fieldFormulatedMaterialSubtypes);

		return this.inherited(arguments);
	},

	/**
	 * @override
	 * Hide product subtype fields if "Product Type" field is empty
	 * @param fields 
	 */
	_buildFields(fields: Field[]) {
		this.inherited(arguments);

		const productType = this.defView.getFieldValue(PRODUCT_TYPE);

		this.setFieldHidden(locale.resource(FOOD_SUBTYPE), !productType);
		this.setFieldHidden(locale.resource(FORMULATED_MATERIAL_SUBTYPE), !productType);
	},

	/**
	 * Hide subtype fields based on selected Product Type.
	 * @param formView 
	 * @param field 
	 * @param value 
	 */
	onChangeProductType(formView: FormView, field: Field, value: any) {
		const productType = xml.enumGetText(value);

		this.setFieldHidden(locale.resource(FOOD_SUBTYPE), !(productType === "Food"));
		this.setFieldHidden(locale.resource(FORMULATED_MATERIAL_SUBTYPE), !(productType === "FormulatedMaterial"));
	},

	/**
	 * @override
	 * Create field store depending on type of field
	 */
	getFieldStore(field, value) {
		if (field.m_label === locale.resource(PRODUCT_TYPE)) {
			return this.getProductTypeStore(field);
		} else if (field.m_label === locale.resource(FOOD_SUBTYPE, [locale.typePlural(FOOD_SUBTYPE)])){
			return this.getFoodSubtypeStore(field);
		} else if (field.m_label === locale.resource(FORMULATED_MATERIAL_SUBTYPE, [locale.typePlural(FORMULATED_MATERIAL_SUBTYPE)])){
			return this.getFormulatedMaterialSubtypeStore(field);
		}
		return this.inherited(arguments);
	},

	/**
	 * Create field store for ProductType field
	 * @param field 
	 */
	getProductTypeStore(field: Field) {
		const ctx = this.getContext();
		const itemsProductTypes:Array<string> = this.getItemsProductTypes(ctx);

		return new Names({
			data: array.map(itemsProductTypes, (typeName) => {
				return csiStore.nameObject(locale.type(xml.enumGetText(typeName)), typeName);
			}),
		});
	},

	/**
	 * Get field store for Food Subtype field
	 * @param field 
	 * @returns 
	 */
	getFoodSubtypeStore(field: Field) {
		const q = new Query()
			.Attr("Active", xml.OP_EQUALS, true, "Child:Settings");
		return this.getProductSubtypeStore(field, q, FOOD_SUBTYPE);
	},

	/**
	 * Get field store for FormulatedMaterial Subtype field
	 * @param field 
	 * @returns 
	 */
	getFormulatedMaterialSubtypeStore(field: Field) {
		const q = new Query()
			.Attr("Active", xml.OP_EQUALS, true, "Child:Settings");
		return this.getProductSubtypeStore(field, q, FORMULATED_MATERIAL_SUBTYPE);
	},

	/**
	 * Create field store for ProductSubtype field
	 * @param field 
	 */
	getProductSubtypeStore(field: Field, query: Query, productType: String) {
		const ctx = this.getContext();

		const rel = lang.mixin(new Relationship(), {
			m_typeNode: productType,
			m_query: query,
		});
		
		const store = ctx.view.createHeadingNamesStore(ctx, field, null, rel);
		return store;
	},

	/**
	 * Get ItemsProductTypes allowed for BaseBOM
	 * @param ctx 
	 */
	getItemsProductTypes(ctx: Context): Array<string> {
		const revisionNode = ctx.getNode();
		const baseBOMNode = ctx.cache.getNode(revisionNode.__Parent__);
		const baseBOMSubtype = ctx.cache.getNode(baseBOMNode.Subtype);
		const baseBOMSubtypeSetting = ctx.cache.getNode(baseBOMSubtype.Settings);
		const itemsProductTypes:Array<string> = baseBOMSubtypeSetting.ItemsProductTypes;

		return itemsProductTypes;
	},
    
});

export = SelectBOMItemProductForm;